import { For, Show, Suspense, createMemo } from "solid-js";
import { A, createAsync, type RouteDefinition } from "@solidjs/router";
import { AppShell, PageHeader } from "~/components/layout";
import { Button, Card, CardContent, Skeleton, EmptyState, Badge, ClientOnly } from "~/components/ui";
import { getNotifications, markAllNotificationsRead } from "~/server/bff";
import { formatRelativeTime } from "~/lib/utils";
import { Bell, BellOff, Check, Trash2, Settings } from "lucide-solid";

export const route = {
  preload: () => getNotifications(),
} satisfies RouteDefinition;

export default function NotificationsPage() {
  if (import.meta.env.DEV) {
    console.info("[debug] render NotificationsPage");
  }
  const notifications = createAsync(() => getNotifications(), { deferStream: true });

  const unreadNotifications = createMemo(() => {
    const list = notifications();
    if (!list) return null;
    return list.filter((n) => !n.isRead);
  });
  const readNotifications = createMemo(() => {
    const list = notifications();
    if (!list) return null;
    return list.filter((n) => n.isRead);
  });

  return (
    <AppShell title="Notifications">
      <div class="p-6 max-w-3xl mx-auto space-y-6">
        <PageHeader
          title="Notifications"
          description="Stay updated on important activity"
          actions={
            <ClientOnly>
              <div class="flex gap-2">
                <form action={markAllNotificationsRead} method="post">
                  <Button type="submit" variant="outline" size="sm">
                    <Check class="h-4 w-4 mr-1" />
                    Mark all read
                  </Button>
                </form>
                <A href="/settings/notifications" aria-label="Notification settings">
                  <Button variant="ghost" size="sm" aria-label="Notification settings" title="Notification settings">
                    <Settings class="h-4 w-4" />
                  </Button>
                </A>
              </div>
            </ClientOnly>
          }
        />

        <Suspense fallback={<NotificationsSkeleton />}>
          <Show when={notifications()} fallback={<NotificationsSkeleton />}>
            {(items) => (
              <Show
                when={items().length > 0}
                fallback={
                  <EmptyState
                    icon={<BellOff class="h-12 w-12" />}
                    title="No notifications"
                    description="You're all caught up! Notifications will appear here."
                  />
                }
              >
                <div class="space-y-8">
                  <Show when={unreadNotifications() && unreadNotifications()!.length > 0}>
                    <section aria-label="Unread notifications" class="space-y-3">
                      <h2 class="text-sm font-medium text-muted-foreground">Unread</h2>
                      <div class="space-y-2">
                        <For each={unreadNotifications() ?? []}>
                          {(notification) => (
                            <A href={notification.actionUrl || "#"}>
                              <Card class="group hover:border-primary/50 transition-colors">
                                <CardContent class="p-4 flex items-start gap-3">
                                  <div class="mt-1">
                                    <Bell class="h-4 w-4 text-primary" />
                                  </div>
                                  <div class="flex-1 min-w-0 space-y-1">
                                    <div class="flex items-center justify-between gap-2">
                                      <h3 class="font-medium text-sm">{notification.title}</h3>
                                      <span class="inline-flex items-center gap-1 text-xs text-muted-foreground">
                                        <span class="h-2 w-2 rounded-full bg-primary" />
                                        {formatRelativeTime(notification.createdAt)}
                                      </span>
                                    </div>
                                    <p class="text-sm text-muted-foreground line-clamp-2">
                                      {notification.message}
                                    </p>
                                    <Show when={notification.type}>
                                      <Badge variant="outline" class="text-[10px] uppercase tracking-wide">
                                        {notification.type}
                                      </Badge>
                                    </Show>
                                  </div>
                                </CardContent>
                              </Card>
                            </A>
                          )}
                        </For>
                      </div>
                    </section>
                  </Show>

                  <Show when={readNotifications() && readNotifications()!.length > 0}>
                    <section aria-label="Earlier notifications" class="space-y-3">
                      <h2 class="text-sm font-medium text-muted-foreground">Earlier</h2>
                      <div class="space-y-2">
                        <For each={readNotifications() ?? []}>
                          {(notification) => (
                            <A href={notification.actionUrl || "#"}>
                              <Card class="group hover:border-primary/50 transition-colors opacity-75 hover:opacity-100">
                                <CardContent class="p-4 flex items-start gap-3">
                                  <div class="mt-1">
                                    <Bell class="h-4 w-4 text-muted-foreground" />
                                  </div>
                                  <div class="flex-1 min-w-0 space-y-1">
                                    <div class="flex items-center justify-between gap-2">
                                      <h3 class="font-medium text-sm">{notification.title}</h3>
                                      <span class="text-xs text-muted-foreground">
                                        {formatRelativeTime(notification.createdAt)}
                                      </span>
                                    </div>
                                    <p class="text-sm text-muted-foreground line-clamp-2">
                                      {notification.message}
                                    </p>
                                    <Show when={notification.type}>
                                      <Badge variant="outline" class="text-[10px] uppercase tracking-wide">
                                        {notification.type}
                                      </Badge>
                                    </Show>
                                  </div>
                                </CardContent>
                              </Card>
                            </A>
                          )}
                        </For>
                      </div>
                    </section>
                  </Show>
                </div>
              </Show>
            )}
          </Show>
        </Suspense>
      </div>
    </AppShell>
  );
}

function NotificationsSkeleton() {
  return (
    <div class="space-y-2">
      <For each={[1, 2, 3, 4, 5]}>
        {() => (
          <Card>
            <CardContent class="p-4">
              <div class="flex items-start gap-3">
                <Skeleton class="h-10 w-10 rounded-md" />
                <div class="flex-1 space-y-2">
                  <Skeleton class="h-4 w-48" />
                  <Skeleton class="h-4 w-full" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </For>
    </div>
  );
}
